import { canApprove, isCancelAsk, isUnbooked, type PtoRequestView } from "@/lib/team/pto-list";
import { ApiError } from "@/lib/time/api-client";

/**
 * Which PTO rows are mid-decision, per request id, so one row's write never re-enables another's
 * buttons, and a decided row never offers Approve or Reject again before the list catches up.
 *
 * Every lock and error is keyed by request id AND the row's version when the decision started
 * (`rowVersion`: status, booked days, last change), and lives only while the list still shows
 * that version:
 * - running: the call is in flight (held whatever the list shows).
 * - done: the call succeeded; held while the list still shows the version it acted on and the
 *   row is still to do (pending for approve/reject, unbooked for book, a cancel ask for keep,
 *   approved or a cancel ask for remove).
 * - uncertain: a 504, so it may have saved; held until a list read after the failure lands.
 * `pruneDecisions` drops settled locks and stale errors on each list read, so a row whose state
 * changes again later (a new cancel ask after Keep) is actionable even if it reads the same.
 */
export type DecisionAction = "approve" | "reject" | "book" | "keep" | "remove";

type Lock =
  | { phase: "running"; action: DecisionAction; version: string }
  | { phase: "done"; action: DecisionAction; version: string }
  | { phase: "uncertain"; action: DecisionAction; version: string; at: number };

interface RowError {
  message: string;
  version: string;
}

type Versioned = Pick<PtoRequestView, "id" | "status" | "bookedDays" | "changedOn">;

/** The request state a decision acts on; a different value means the row has moved on. */
function rowVersion(r: Versioned): string {
  return `${r.status}|${r.bookedDays}|${r.changedOn ?? ""}`;
}

export interface DecisionState {
  locks: Readonly<Record<string, Lock>>;
  errors: Readonly<Record<string, RowError>>;
}

export const NO_DECISIONS: DecisionState = { locks: {}, errors: {} };

export const MAY_HAVE_SAVED_TEXT =
  "That took too long and may have saved. Refresh before trying again.";

const isTimeout = (err: Error) => err instanceof ApiError && err.status === 504;

/** The text for a failed decision, the same in the toast and on the row. */
export function decisionErrorText(err: Error): string {
  return isTimeout(err) ? MAY_HAVE_SAVED_TEXT : err.message;
}

function without<T>(record: Readonly<Record<string, T>>, id: string): Record<string, T> {
  const { [id]: _drop, ...rest } = record;
  return rest;
}

export function startDecision(
  s: DecisionState,
  r: Versioned,
  action: DecisionAction,
): DecisionState {
  return {
    locks: { ...s.locks, [r.id]: { phase: "running", action, version: rowVersion(r) } },
    errors: without(s.errors, r.id),
  };
}

export function decisionSucceeded(s: DecisionState, id: string): DecisionState {
  const lock = s.locks[id];
  if (!lock) return s;
  return {
    ...s,
    locks: { ...s.locks, [id]: { phase: "done", action: lock.action, version: lock.version } },
  };
}

export function decisionFailed(
  s: DecisionState,
  id: string,
  err: Error,
  now: number,
): DecisionState {
  const lock = s.locks[id];
  if (!lock) return s;
  if (isTimeout(err)) {
    return {
      locks: {
        ...s.locks,
        [id]: { phase: "uncertain", action: lock.action, version: lock.version, at: now },
      },
      errors: without(s.errors, id),
    };
  }
  return {
    locks: without(s.locks, id),
    errors: { ...s.errors, [id]: { message: err.message, version: lock.version } },
  };
}

function stillToDo(action: DecisionAction, r: PtoRequestView): boolean {
  switch (action) {
    case "book":
      return isUnbooked(r);
    case "keep":
      return isCancelAsk(r);
    case "remove":
      return r.status === "approved" || isCancelAsk(r);
    default:
      return canApprove(r);
  }
}

/** True only while the row's own write is in flight (a 504 hold is not "running"). */
export function isDecisionRunning(s: DecisionState, id: string): boolean {
  return s.locks[id]?.phase === "running";
}

/** True when the error means the write may have saved (a 504). */
export function isMayHaveSaved(err: Error): boolean {
  return isTimeout(err);
}

/** Whether a lock still holds `r`, as the list shows it (`listUpdatedAt` is when it was read). */
function holds(lock: Lock, r: PtoRequestView | undefined, listUpdatedAt: number): boolean {
  switch (lock.phase) {
    case "running":
      return true;
    case "done":
      return r !== undefined && rowVersion(r) === lock.version && stillToDo(lock.action, r);
    case "uncertain":
      return listUpdatedAt <= lock.at;
  }
}

/**
 * Drops every lock the list read at `listUpdatedAt` shows settled (the decision landed, the row
 * moved on or left the list, or a 504's hold is older than the read) and every error whose row
 * moved on. Running calls are kept. Returns `s` itself when nothing changes.
 */
export function pruneDecisions(
  s: DecisionState,
  rows: readonly PtoRequestView[],
  listUpdatedAt: number,
): DecisionState {
  const byId = new Map(rows.map((r) => [r.id, r]));
  let locks: Record<string, Lock> | null = null;
  for (const [id, lock] of Object.entries(s.locks)) {
    if (holds(lock, byId.get(id), listUpdatedAt)) continue;
    locks = without(locks ?? s.locks, id);
  }
  let errors: Record<string, RowError> | null = null;
  for (const [id, e] of Object.entries(s.errors)) {
    const r = byId.get(id);
    if (r && rowVersion(r) === e.version) continue;
    errors = without(errors ?? s.errors, id);
  }
  if (!locks && !errors) return s;
  return { locks: locks ?? s.locks, errors: errors ?? s.errors };
}

/**
 * Whether a row's buttons are disabled and what its inline line says, given the list as it stands
 * (`listUpdatedAt` is when that list was read). A lock or error made for another version of the
 * row never applies.
 */
export function rowDecisionState(
  s: DecisionState,
  r: PtoRequestView,
  listUpdatedAt: number,
): { busy: boolean; error: string | null } {
  const lock = s.locks[r.id];
  const e = s.errors[r.id];
  const error = e && e.version === rowVersion(r) ? e.message : null;
  if (!lock) return { busy: false, error };
  if (lock.phase === "running") return { busy: true, error: null };
  if (lock.phase === "uncertain") {
    return holds(lock, r, listUpdatedAt)
      ? { busy: true, error: MAY_HAVE_SAVED_TEXT }
      : { busy: false, error: null };
  }
  return { busy: holds(lock, r, listUpdatedAt), error: null };
}
